import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { CustomDropdown } from "../../Common/_components/CustomDropdown";
import { CustomNumberInput } from "../../Common/_components/CustomNumberInput";
import { FileUploadComponent, SimpleToggle } from "../../Everou/_components";
import { Icons } from "../../Everou/_assets";
import {
    OrganizationLocationSettings,
    makeOrganizationLocationSettings,
} from "../../Organizations/_models/Organization";
import { PMSOrganizationSettingsActions } from "../_actions/PMSOrganizationSettingsActions";
import { PMSLocationEmbedDomains } from "../ModuleLocations/_components/PMSLocationEmbedDomains";
import {
    InlineValidatedTextInput,
    SettingsRow,
    SettingsSection,
} from "../ModuleLocations/_components/PMSLocationSettingsFields";

const LOCATION_RADIUS_MIN = 50;
const LOCATION_RADIUS_MAX = 10000;
const SECURITY_ALERT_DEFAULT_MINUTES = 15;
const SECURITY_ALERT_MAX_MINUTES = 546;

type Props = {
    organizationUid: string;
    settings?: OrganizationLocationSettings;
    section: "ACCESS" | "ALARM";
};

// These shared controls are implemented in JSX, whose null defaults make
// TypeScript infer overly narrow props. Their runtime API intentionally
// accepts the richer values used below (the location screen already does).
const OrganizationFileUpload: any = FileUploadComponent;
const OrganizationTextInput: any = InlineValidatedTextInput;
const OrganizationEmbedDomains: any = PMSLocationEmbedDomains;

export function PMSOrganizationConfiguration({
    organizationUid,
    settings,
    section,
}: Props) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const effectiveSettings = settings ?? makeOrganizationLocationSettings();
    const [local, setLocal] = useState(effectiveSettings);
    const [isUploadingVideo, setIsUploadingVideo] = useState(false);
    const [showVideoPreview, setShowVideoPreview] = useState(false);

    useEffect(() => setLocal(effectiveSettings), [settings]);

    function update(fields: Record<string, unknown>) {
        return dispatch(
            PMSOrganizationSettingsActions.update(organizationUid, fields),
        );
    }

    function updateLocal<K extends keyof OrganizationLocationSettings>(
        key: K,
        value: OrganizationLocationSettings[K],
        serverKey: string,
    ) {
        setLocal((previous) => ({ ...previous, [key]: value }));
        update({ [serverKey]: value });
    }

    async function handleUploadVideo(video) {
        setIsUploadingVideo(true);
        try {
            await dispatch(
                PMSOrganizationSettingsActions.uploadVideo(
                    organizationUid,
                    video,
                ),
            );
        } finally {
            setIsUploadingVideo(false);
        }
    }

    async function handleDeleteVideo() {
        setIsUploadingVideo(true);
        try {
            await dispatch(
                PMSOrganizationSettingsActions.deleteVideo(organizationUid),
            );
            setShowVideoPreview(false);
        } finally {
            setIsUploadingVideo(false);
        }
    }

    const locationModeOptions = [
        CustomDropdown.buildOption(
            "off",
            t("mod_pms_location_location_check_mode_off"),
        ),
        CustomDropdown.buildOption(
            "warn",
            t("mod_pms_location_location_check_mode_warn"),
        ),
        CustomDropdown.buildOption(
            "required",
            t("mod_pms_location_location_check_mode_required"),
        ),
    ];

    const securityAlertEnabled = local.securityAlertTime > 0;
    const securityAlertMinutes = securityAlertEnabled
        ? Math.round(local.securityAlertTime / 60)
        : SECURITY_ALERT_DEFAULT_MINUTES;

    return (
        <div>
            <div className="d-flex align-items-center c-bg-grey-250 c-rounded-big p-3 mb-3">
                <img
                    src={Icons.info}
                    alt="info"
                    className="mr-2 flex-shrink-0"
                    style={{ width: "24px", height: "24px" }}
                />
                <span>{t("mod_organizations_location_settings_note")}</span>
            </div>

            {section === "ACCESS" && <SettingsSection
                title={t("mod_pms_location_public_link_section_title")}
            >
                <SettingsRow
                    label={t(
                        "mod_pms_location_invitation_link_add_to_home_title",
                    )}
                    tooltip={t(
                        "mod_pms_location_invitation_link_section_tooltip",
                    )}
                >
                    <SimpleToggle
                        className="c-toggle-smooth"
                        checked={local.invitationLinkAddToHome}
                        handleChange={({ detail: { value } }) =>
                            updateLocal(
                                "invitationLinkAddToHome",
                                value,
                                "invitation_link_add_to_home",
                            )
                        }
                    />
                </SettingsRow>
                {local.invitationLinkAddToHome && (
                    <SettingsRow
                        label={t(
                            "mod_pms_location_invitation_link_app_name_title",
                        )}
                    >
                        <OrganizationTextInput
                            key={local.invitationLinkAppName}
                            initialValue={local.invitationLinkAppName}
                            placeholder="Tripkey"
                            required={false}
                            maxLength={12}
                            handleSave={(value) =>
                                update({ invitation_link_app_name: value })
                            }
                        />
                    </SettingsRow>
                )}
                <SettingsRow
                    label={t("mod_pms_location_location_check_mode_title")}
                    tooltip={t(
                        "mod_pms_location_location_check_mode_section_tooltip",
                    )}
                >
                    <div className="d-flex align-items-center">
                        <div style={{ width: "15rem" }}>
                            <CustomDropdown
                                value={local.invitationLinkLocationCheckMode}
                                options={locationModeOptions}
                                mode="to-left"
                                isSearchable={false}
                                onChange={(value) =>
                                    updateLocal(
                                        "invitationLinkLocationCheckMode",
                                        value,
                                        "invitation_link_location_check_mode",
                                    )
                                }
                            />
                        </div>
                        {local.invitationLinkLocationCheckMode !== "off" && (
                            <>
                                <span
                                    className="text-secondary mr-2 flex-shrink-0"
                                    style={{ marginLeft: "2rem" }}
                                >
                                    {t(
                                        "mod_pms_location_location_radius_title",
                                    )}
                                </span>
                                <CustomNumberInput
                                    value={local.invitationLinkLocationRadius}
                                    min={1}
                                    max={LOCATION_RADIUS_MAX}
                                    onChange={(value) =>
                                        setLocal((previous) => ({
                                            ...previous,
                                            invitationLinkLocationRadius: value,
                                        }))
                                    }
                                    onBlur={(value) => {
                                        const radius = Math.min(
                                            Math.max(
                                                Math.round(value || 0),
                                                LOCATION_RADIUS_MIN,
                                            ),
                                            LOCATION_RADIUS_MAX,
                                        );
                                        updateLocal(
                                            "invitationLinkLocationRadius",
                                            radius,
                                            "invitation_link_location_radius",
                                        );
                                    }}
                                />
                            </>
                        )}
                    </div>
                </SettingsRow>
                <SettingsRow
                    label={t(
                        "mod_pms_location_service_recommendations_setting_title",
                    )}
                    tooltip={t(
                        "mod_pms_location_public_link_section_tooltip",
                    )}
                >
                    <OrganizationTextInput
                        key={local.urlRecommendations}
                        initialValue={local.urlRecommendations}
                        required={false}
                        className="flex-grow-1 ml-3"
                        inputClassName="flex-grow-1"
                        handleSave={(value) =>
                            update({ url_recommendations: value })
                        }
                    />
                </SettingsRow>
                <SettingsRow
                    label={t("mod_pms_location_video_title")}
                    tooltip={t("mod_pms_location_video_section_tooltip")}
                >
                    <div
                        className="d-flex align-items-start flex-grow-1 ml-3"
                        style={{ columnGap: "12px" }}
                    >
                        <div style={{ flex: "1 1 0", minWidth: 0 }}>
                            <OrganizationFileUpload
                                onUpload={handleUploadVideo}
                                isUploading={isUploadingVideo}
                                fileType=".mp4,.webm,video/mp4,video/webm"
                                fileName={
                                    local.videoUrl
                                        ? t(
                                              "mod_pms_location_video_uploaded",
                                          )
                                        : null
                                }
                                onRemove={handleDeleteVideo}
                                actions={
                                    local.videoUrl ? (
                                        <span
                                            className="cp c-text-primary c-font-weight-medium"
                                            onClick={() =>
                                                setShowVideoPreview(
                                                    (value) => !value,
                                                )
                                            }
                                        >
                                            {t(
                                                showVideoPreview
                                                    ? "mod_pms_location_video_hide"
                                                    : "mod_pms_location_video_show",
                                            )}
                                        </span>
                                    ) : null
                                }
                                preview={
                                    showVideoPreview && local.videoUrl ? (
                                        <video
                                            key={local.videoUrl}
                                            src={local.videoUrl}
                                            controls
                                            className="w-100 c-rounded-big bg-dark"
                                            style={{ maxHeight: "15rem" }}
                                        />
                                    ) : null
                                }
                            />
                            <small className="d-block mt-1 text-secondary">
                                {t("mod_pms_location_video_requirements")}
                            </small>
                        </div>
                        <div
                            className="d-flex align-items-center"
                            style={{ flex: "1 1 0", minWidth: 0 }}
                        >
                            <span className="text-secondary mr-2 flex-shrink-0">
                                {t("mod_pms_location_video_or_url")}
                            </span>
                            <OrganizationTextInput
                                key={local.externalVideoUrl}
                                initialValue={local.externalVideoUrl}
                                required={false}
                                className="flex-grow-1"
                                inputClassName="flex-grow-1"
                                handleSave={(value) =>
                                    update({ external_video_url: value })
                                }
                            />
                        </div>
                    </div>
                </SettingsRow>
            </SettingsSection>}

            {section === "ACCESS" && <SettingsSection
                title={t("mod_pms_location_stay_section_title")}
            >
                {toggleRow(
                    "mod_pms_location_early_checkin_section_title",
                    "mod_pms_location_early_checkin_section_tooltip",
                    local.earlyCheckinEnabled,
                    (value) =>
                        updateLocal(
                            "earlyCheckinEnabled",
                            value,
                            "early_checkin_enabled",
                        ),
                )}
                {toggleRow(
                    "mod_pms_location_early_checkout_section_title",
                    "mod_pms_location_early_checkout_section_tooltip",
                    local.earlyCheckoutEnabled,
                    (value) =>
                        updateLocal(
                            "earlyCheckoutEnabled",
                            value,
                            "early_checkout_enabled",
                        ),
                )}
            </SettingsSection>}

            {section === "ALARM" && <SettingsSection
                title={t("mod_pms_location_auto_protect_section_title")}
                tooltip={t("mod_pms_location_auto_protect_section_tooltip")}
            >
                <div className="text-secondary mt-1 mb-2">
                    {t("mod_pms_location_auto_protect_explanation")}
                </div>
                {toggleRow(
                    "mod_pms_location_auto_protect_enabled_title",
                    null,
                    local.protectAutoEnabledWithoutReservations,
                    (value) =>
                        updateLocal(
                            "protectAutoEnabledWithoutReservations",
                            value,
                            "protect_auto_enabled_without_reservations",
                        ),
                )}
            </SettingsSection>}

            {section === "ALARM" && <SettingsSection
                title={t("mod_pms_location_protect_guest_section_title")}
                tooltip={t("mod_pms_location_protect_guest_section_tooltip")}
            >
                {toggleRow(
                    "mod_pms_location_protect_guest_enabled_title",
                    null,
                    local.enableProtectGuest,
                    (value) =>
                        updateLocal(
                            "enableProtectGuest",
                            value,
                            "enable_protect_guest",
                        ),
                )}
            </SettingsSection>}

            {section === "ALARM" && <SettingsSection
                title={t("mod_pms_location_security_alert_section_title")}
                tooltip={t("mod_pms_location_security_alert_section_tooltip")}
            >
                {toggleRow(
                    "mod_pms_location_security_alert_enabled_title",
                    null,
                    securityAlertEnabled,
                    (value) => {
                        const seconds = value
                            ? SECURITY_ALERT_DEFAULT_MINUTES * 60
                            : 0;
                        updateLocal(
                            "securityAlertTime",
                            seconds,
                            "security_alert_time",
                        );
                    },
                )}
                {securityAlertEnabled && (
                    <SettingsRow
                        label={t(
                            "mod_pms_location_security_alert_minutes_title",
                        )}
                    >
                        <CustomNumberInput
                            value={securityAlertMinutes}
                            min={1}
                            max={SECURITY_ALERT_MAX_MINUTES}
                            onChange={(value) =>
                                setLocal((previous) => ({
                                    ...previous,
                                    securityAlertTime: value * 60,
                                }))
                            }
                            onBlur={(value) => {
                                const minutes = Math.min(
                                    Math.max(Math.round(value || 0), 1),
                                    SECURITY_ALERT_MAX_MINUTES,
                                );
                                updateLocal(
                                    "securityAlertTime",
                                    minutes * 60,
                                    "security_alert_time",
                                );
                            }}
                        />
                    </SettingsRow>
                )}
            </SettingsSection>}

            {section === "ACCESS" && <SettingsSection>
                <OrganizationEmbedDomains
                    organizationUid={organizationUid}
                    settings={local}
                    initialDomains={local.embedDomains}
                    onUpdateOrganization={update}
                />
            </SettingsSection>}
        </div>
    );

    function toggleRow(
        labelKey: string,
        tooltipKey: string | null,
        checked: boolean,
        onChange: (value: boolean) => void,
    ) {
        return (
            <SettingsRow
                label={t(labelKey)}
                tooltip={tooltipKey ? t(tooltipKey) : null}
            >
                <SimpleToggle
                    className="c-toggle-smooth"
                    checked={checked}
                    handleChange={({ detail: { value } }) => onChange(value)}
                />
            </SettingsRow>
        );
    }
}
